import { FC, useMemo } from 'react'
import { v4 as uuidv4 } from 'uuid'

import PinIcon from 'src/components/ui/atoms/Icons/PinIcon'
import UnPinIcon from 'src/components/ui/atoms/Icons/UnPinIcon'
import ItemWithTooltip from 'src/components/ui/molecules/ItemWithTooltip'
import useToggleFavoriteDashboard from 'src/hooks/useToggleFavoriteDashboard'
import { cn } from 'src/lib/cn'
import { AppMenuSavedDashboardInterface } from 'src/types/AppMenuInterface'

interface ProductFavoriteButtonProps {
  subProductCode: string
  program: string | undefined
  dashboard: Pick<AppMenuSavedDashboardInterface, 'id' | 'name' | 'favorite' | 'program_id'>
  className?: string
  iconClassName?: string
  enabled?: boolean
}

const ProductFavoriteButton: FC<ProductFavoriteButtonProps> = ({
  subProductCode,
  program,
  dashboard,
  className,
  iconClassName,
  enabled,
}) => {
  const { handleSetDefaultDashboard, loading, isFavorite } = useToggleFavoriteDashboard({
    subProductCode,
    program,
    dashboard,
  })

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    e.preventDefault()
    if (loading || !enabled) return
    handleSetDefaultDashboard()
  }

  const id = useMemo(() => `product-favorite-button-${dashboard.id}-${uuidv4()}`, [dashboard.id])

  if (!enabled) {
    return <div className={cn('min-w-4', className)} />
  }

  return (
    <div data-testid="product-favorite-button" role="button" className={cn('min-w-4', className)}>
      <ItemWithTooltip
        item={
          <div
            className={cn(
              'favorite-icon transition-[margin] duration-300 ease-in-out',
              iconClassName,
            )}
            onClick={handleClick}
          >
            {isFavorite ? <PinIcon /> : <UnPinIcon />}
          </div>
        }
        tooltipProps={{
          className: 'pointer-events-none!',
          clickable: false,
          place: 'top',
        }}
        id={id}
      >
        {isFavorite ? 'Unpin' : 'Pin'}
      </ItemWithTooltip>
    </div>
  )
}

export default ProductFavoriteButton
